
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { RotateCw, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface SyncProfilesButtonProps {
  onSyncComplete?: () => void;
}

const SyncProfilesButton: React.FC<SyncProfilesButtonProps> = ({ onSyncComplete }) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const { toast } = useToast();

  const handleSyncClick = async () => {
    setIsSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('twitter-sync-kol-profiles', {
        method: 'POST',
        body: {}
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: 'Twitter Profiles Synchronized',
          description: `${data.results.success} profiles updated, ${data.results.failed} failed, ${data.results.skipped} skipped`,
          variant: 'default',
          action: (
            <div className="h-8 w-8 bg-green-50 rounded-full flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
          ),
        });
        
        // Notify parent component that sync is complete
        if (onSyncComplete) {
          onSyncComplete();
        }
      } else {
        throw new Error(data.message || 'Failed to sync Twitter profiles');
      }
    } catch (err: any) {
      console.error('Error syncing Twitter profiles:', err);
      toast({
        title: 'Sync Failed',
        description: err.message || 'There was an error syncing Twitter profiles',
        variant: 'destructive',
        action: (
          <div className="h-8 w-8 bg-red-50 rounded-full flex items-center justify-center">
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
        ),
      });
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="h-9 flex items-center gap-2"
      onClick={handleSyncClick}
      disabled={isSyncing}
    >
      <RotateCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
      <span>{isSyncing ? 'Syncing...' : 'Sync Twitter Profiles'}</span>
    </Button>
  );
};

export default SyncProfilesButton;
